import { useState, useEffect } from "react";
import { createBrowserRouter, RouterProvider, Outlet } from "react-router-dom";

import {
  AuthChangeRedirector,
  AuthenticatedRoute,
} from "@sharpana/auth/AuthRoutingWrappers";
import { useConfig } from "@sharpana/auth/authHooks";
import { getAuthRoutes } from "@sharpana/auth/routes";

import { CoachingHome } from "./screens/Home/CoachingHome";
import { CoachingSessionPage } from "./screens/CoachingSession/CoachingSessionPage";
import { ArcGenerationSession } from "./screens/ArcGenerationSession/ArcGenerationSession";
import { AtlasPage } from "./screens/Atlas/AtlasPage";
import { LibraryPage } from "./screens/Library/LibraryPage";
import Paywall from "./pages/Paywall";
import RevcatPostCheckout from "./pages/RevcatPostCheckout";
import Settings from "./pages/Settings";


function Root() {
  // Minimal coaching shell.  No NavBar today — the coaching product's
  // shell will be filled in when the rest of the layout is brought
  // over.  This wrapper exists so AuthChangeRedirector can intercept
  // auth state transitions at the top of the tree.
  return (
    <main className="flex-shrink-0">
      <Outlet />
    </main>
  );
}


function createRouter(config: any) {
  // Auth routes (/account/login, /account/signup, etc.) come from the
  // shared auth package — same flows boilit uses, just rendered inside
  // the coaching app shell.
  const authRoutes = getAuthRoutes(config);

  return createBrowserRouter([
    {
      path: "/",
      element: (
        <AuthChangeRedirector>
          <Root />
        </AuthChangeRedirector>
      ),
      children: [
        {
          path: "/",
          element: (
            <AuthenticatedRoute>
              <CoachingHome />
            </AuthenticatedRoute>
          ),
        },
        {
          path: "/coaching",
          element: (
            <AuthenticatedRoute>
              <CoachingHome />
            </AuthenticatedRoute>
          ),
        },
        {
          // The sky atlas — every ring, planet and claim the registry
          // holds, read against this learner's own folded state.
          path: "/atlas",
          element: (
            <AuthenticatedRoute>
              <AtlasPage />
            </AuthenticatedRoute>
          ),
        },
        {
          path: "/coaching/atlas",
          element: (
            <AuthenticatedRoute>
              <AtlasPage />
            </AuthenticatedRoute>
          ),
        },
        {
          // The instrument library — the press's reviewing surface.
          // Wrapped in AuthenticatedRoute like every other route, but
          // the STAFF gate is the API's: a route guard decides what is
          // drawn and cannot decide what is served, so the page reads
          // the 403 and says so rather than pretending the door is here.
          path: "/coaching/library",
          element: (
            <AuthenticatedRoute>
              <LibraryPage />
            </AuthenticatedRoute>
          ),
        },
        {
          path: "/library",
          element: (
            <AuthenticatedRoute>
              <LibraryPage />
            </AuthenticatedRoute>
          ),
        },
        {
          path: "/coaching/session/:sessionId?",
          element: (
            <AuthenticatedRoute>
              <CoachingSessionPage engine="generative" />
            </AuthenticatedRoute>
          ),
        },
        {
          // The Session-API runtime (authored activities).  Two ways to
          // start: ?ref=<activity or catalog-unit ref> pins one unit;
          // ?recipe=<id>[&planet=&claim=&minutes=] hands the choosing to
          // a recipe.  Resume by session id.
          path: "/coaching/activity/:sessionId?",
          element: (
            <AuthenticatedRoute>
              <CoachingSessionPage engine="runtime" />
            </AuthenticatedRoute>
          ),
        },
        {
          path: "/arc-generation/:sessionId?",
          element: (
            <AuthenticatedRoute>
              <ArcGenerationSession />
            </AuthenticatedRoute>
          ),
        },
        {
          // Billing surfaces.
          //
          // /revcat-post-checkout IS wire contract: the wrapper navigates
          // here after a native purchase and holds a blocking full-screen
          // overlay until this page confirms, so its name must match
          // `postPurchaseRoute` in mobile/shell/products/zairai.ts.
          //
          // /settings is NOT — the wrapper only tracks that path to toggle a
          // debug overlay that is currently commented out.  It is here
          // because Apple guideline 3.1.2 wants an in-app route to manage a
          // subscription bought in-app, which is a product requirement
          // rather than a coupling.
          path: "/purchase",
          element: (
            <AuthenticatedRoute>
              <Paywall />
            </AuthenticatedRoute>
          ),
        },
        {
          path: "/revcat-post-checkout",
          element: (
            <AuthenticatedRoute>
              <RevcatPostCheckout />
            </AuthenticatedRoute>
          ),
        },
        {
          path: "/settings",
          element: (
            <AuthenticatedRoute>
              <Settings />
            </AuthenticatedRoute>
          ),
        },
        ...authRoutes,
      ],
    },
  ]);
}

export default function Router() {
  // Same pattern boilit uses: defer router creation until auth config
  // is loaded so route loaders don't fire before <AuthContext/> has
  // bootstrapped.
  const [router, setRouter] = useState<any>(null);
  const config = useConfig();
  useEffect(() => {
    setRouter(createRouter(config));
  }, [config]);
  return router ? <RouterProvider router={router} /> : null;
}
